import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useCompanyOptions } from '../../hooks/useCompanyOptions';
import { supabase } from '../../lib/supabase';
import type { Contact } from '../../types/contact';

interface EditContactPopupProps {
  contact: Contact;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditContactPopup({ contact, onClose, onSuccess }: EditContactPopupProps) {
  const { options: companyOptions } = useCompanyOptions();
  const [formData, setFormData] = useState({
    first_name: contact.first_name,
    last_name: contact.last_name,
    work_email: contact.work_email || '',
    personal_email: contact.personal_email || '',
    phone_number: contact.phone_number || '',
    work_phone: contact.work_phone || '',
    linkedin_url: contact.linkedin_url || '',
    birthday: contact.birthday ? new Date(contact.birthday + 'T00:00:00Z').toISOString().split('T')[0] : '',
    significant_other: contact.significant_other || '',
    children_details: contact.children_details || '',
    notes: contact.notes || '',
    company_id: contact.company_id || '',
    job_title: contact.job_title || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const { error: saveError } = await supabase
        .from('nrm_contacts')
        .update({
          ...formData,
          birthday: formData.birthday || null,
          company_id: formData.company_id || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', contact.id);

      if (saveError) throw saveError;

      onSuccess();
      onClose();
    } catch (err) {
      setError('Failed to update contact. Please try again.');
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-dark-800 rounded-lg max-w-4xl w-full p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Edit Contact</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-300 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="first_name"
                  name="first_name"
                  required
                  value={formData.first_name}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-300 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="last_name"
                  name="last_name"
                  required
                  value={formData.last_name}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="company_id" className="block text-sm font-medium text-gray-300 mb-1">
                  Company
                </label>
                <select
                  id="company_id"
                  name="company_id"
                  value={formData.company_id}
                  onChange={handleChange}
                  className="input w-full"
                >
                  <option value="">Select company...</option>
                  {companyOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="job_title" className="block text-sm font-medium text-gray-300 mb-1">
                  Job Title
                </label>
                <input
                  type="text"
                  id="job_title"
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <div>
                <label htmlFor="work_email" className="block text-sm font-medium text-gray-300 mb-1">
                  Work Email
                </label>
                <input
                  type="email"
                  id="work_email"
                  name="work_email"
                  value={formData.work_email}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="personal_email" className="block text-sm font-medium text-gray-300 mb-1">
                  Personal Email
                </label>
                <input
                  type="email"
                  id="personal_email"
                  name="personal_email"
                  value={formData.personal_email}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="work_phone" className="block text-sm font-medium text-gray-300 mb-1">
                  Work Phone
                </label>
                <input
                  type="tel"
                  id="work_phone"
                  name="work_phone"
                  value={formData.work_phone}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>

              <div>
                <label htmlFor="phone_number" className="block text-sm font-medium text-gray-300 mb-1">
                  Personal Phone
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  className="input w-full"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <div>
              <label htmlFor="linkedin_url" className="block text-sm font-medium text-gray-300 mb-1">
                LinkedIn URL
              </label>
              <input
                type="url"
                id="linkedin_url"
                name="linkedin_url"
                value={formData.linkedin_url}
                onChange={handleChange}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="birthday" className="block text-sm font-medium text-gray-300 mb-1">
                Birthday
              </label>
              <input
                type="date"
                id="birthday"
                name="birthday"
                value={formData.birthday}
                onChange={handleChange}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="significant_other" className="block text-sm font-medium text-gray-300 mb-1">
                Significant Other
              </label>
              <input
                type="text"
                id="significant_other"
                name="significant_other"
                value={formData.significant_other}
                onChange={handleChange}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="children_details" className="block text-sm font-medium text-gray-300 mb-1">
                Children Details
              </label>
              <input
                type="text"
                id="children_details"
                name="children_details"
                value={formData.children_details}
                onChange={handleChange}
                className="input w-full"
              />
            </div>

            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows={4}
                className="input w-full"
              />
            </div>
          </div>

          {error && (
            <div className="text-red-500 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}